'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { MonitorIcon, MoonIcon, SunIcon } from '@phosphor-icons/react';
import type { ThemeMode } from '@/lib/types';
import { LIGHT_ONLY_ROUTE_PREFIXES, THEME_MEDIA_QUERY, THEME_STORAGE_KEY, cn, isLightOnlyRoute } from '@/lib/utils';

const THEME_SCRIPT = `
  const doc = document.documentElement;
  const lightOnlyRoutes = ${JSON.stringify(LIGHT_ONLY_ROUTE_PREFIXES)};
  const forceLight = lightOnlyRoutes.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + "/")
  );
  const theme = localStorage.getItem("${THEME_STORAGE_KEY}") ?? "system";

  if (forceLight) {
    doc.classList.add("light");
  } else if (theme === "system") {
    if (window.matchMedia("${THEME_MEDIA_QUERY}").matches) {
      doc.classList.add("dark");
    } else {
      doc.classList.add("light");
    }
  } else {
    doc.classList.add(theme);
  }
`
  .trim()
  .replace(/\n/g, '')
  .replace(/\s+/g, ' ');

function getStoredTheme(): ThemeMode {
  return (localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode) ?? 'system';
}

// 浅色-only 路由强制浅色；其余路由按用户设置（system 则跟随系统偏好）解析
function resolveTheme(theme: ThemeMode): 'light' | 'dark' {
  if (isLightOnlyRoute(window.location.pathname)) {
    return 'light';
  }

  if (theme === 'system') {
    return window.matchMedia(THEME_MEDIA_QUERY).matches ? 'dark' : 'light';
  }

  return theme;
}

function applyTheme(theme: ThemeMode) {
  const doc = document.documentElement;

  doc.classList.remove('dark', 'light');
  localStorage.setItem(THEME_STORAGE_KEY, theme);
  doc.classList.add(resolveTheme(theme));
}

// 客户端路由切换不会重新执行 <head> 中的内联脚本，需在路由变化时重新同步配色方案
function applyRouteTheme() {
  const doc = document.documentElement;
  const resolved = resolveTheme(getStoredTheme());

  if (doc.classList.contains(resolved)) {
    return;
  }

  doc.classList.remove('dark', 'light');
  doc.classList.add(resolved);
}

interface ThemeToggleProps {
  className?: string;
}

export function ApplyThemeScript() {
  return <script id="theme-script">{THEME_SCRIPT}</script>;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const pathname = usePathname();
  const [theme, setTheme] = useState<ThemeMode | undefined>(undefined);

  useEffect(() => {
    setTheme(getStoredTheme());
  }, []);

  useEffect(() => {
    applyRouteTheme();
  }, [pathname]);

  function handleThemeChange(theme: ThemeMode) {
    applyTheme(theme);
    setTheme(theme);
  }

  // 浅色-only 路由不支持暗色模式，隐藏切换按钮以免切换到不可读的配色
  if (isLightOnlyRoute(pathname)) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-foreground bg-background flex w-full flex-row justify-end divide-x overflow-hidden rounded-full border',
        className
      )}
    >
      <span className="sr-only">Color scheme toggle</span>
      <button
        type="button"
        onClick={() => handleThemeChange('dark')}
        className="cursor-pointer p-1 pl-1.5"
      >
        <span className="sr-only">Enable dark color scheme</span>
        <MoonIcon size={16} weight="bold" className={cn(theme !== 'dark' && 'opacity-25')} />
      </button>
      <button
        type="button"
        onClick={() => handleThemeChange('light')}
        className="cursor-pointer px-1.5 py-1"
      >
        <span className="sr-only">Enable light color scheme</span>
        <SunIcon size={16} weight="bold" className={cn(theme !== 'light' && 'opacity-25')} />
      </button>
      <button
        type="button"
        onClick={() => handleThemeChange('system')}
        className="cursor-pointer p-1 pr-1.5"
      >
        <span className="sr-only">Enable system color scheme</span>
        <MonitorIcon size={16} weight="bold" className={cn(theme !== 'system' && 'opacity-25')} />
      </button>
    </div>
  );
}
